import axios from 'axios';

/**
 * POST to the monitoring backend, tolerating a failed silent token fetch.
 *
 * WHY THIS EXISTS
 * ---------------
 * Both data hooks used to do:
 *
 *     const token = await getAccessTokenSilently();
 *     axios.post(url, body, { headers: { Authorization: `Bearer ${token}` } });
 *
 * `getAccessTokenSilently()` THROWS when Auth0 cannot mint a token without user
 * interaction -- `consent_required`, `login_required`, or an origin missing from
 * the tenant's allow-list. That throw happens BEFORE any request is made, so the
 * UI renders the raw Auth0 error ("Consent required") and the backend is never
 * contacted. On localhost that is guaranteed: the dev origin is not on the
 * allow-list, so the tabs cannot be exercised in a browser at all.
 *
 * This does not weaken anything. The decision to require auth belongs to the
 * BACKEND, not to us -- a request without a bearer token is rejected there with
 * a 401 unless DEV_MODE is set. All this changes is that the backend gets to
 * make that decision instead of the client refusing to ask.
 *
 * The auth failure is not swallowed: if the request then comes back 401/403,
 * the original Auth0 reason is surfaced, because "consent_required" tells you
 * far more than a bare 401 does.
 */
export async function apiPost(baseUrl, getAccessTokenSilently, path, body) {
  let token = null;
  let authError = null;

  try {
    token = await getAccessTokenSilently();
  } catch (err) {
    // Remembered, not swallowed -- see the 401 branch below.
    authError = err?.error || err?.message || String(err);
  }

  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    const res = await axios.post(`${baseUrl}/send/${path}`, body, { headers });
    return res.data;
  } catch (err) {
    const status = err?.response?.status;
    if ((status === 401 || status === 403) && authError) {
      throw new Error(
        `Not signed in (${authError}). The backend rejected the request with ${status}. ` +
        `Locally, start the backend with DEV_MODE=TRUE; in a deployed environment ` +
        `this origin needs to be on the Auth0 allow-list.`
      );
    }
    throw err;
  }
}

export default apiPost;
